<template>
  <ion-modal 
    :is-open="isOpen" 
    @didDismiss="$emit('close')"
  >
    <ion-header>
      <ion-toolbar class="bg-gradient-to-r from-purple-500 to-pink-600">
        <ion-title class="text-white font-bold">
          <div class="flex items-center gap-2">
            <span class="text-2xl">{{ vehicle ? '✏️' : '🚗' }}</span>
            {{ vehicle ? 'Edit Vehicle' : 'Add Vehicle' }}
          </div>
        </ion-title>
        <ion-buttons slot="end">
          <ion-button @click="$emit('close')" class="text-white">
            <ion-icon :icon="closeOutline"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content class="ion-padding bg-gradient-to-br from-purple-50/30 to-pink-50/30 dark:from-purple-900/10 dark:to-pink-900/10">
      <form @submit.prevent="handleSubmit" class="space-y-4 max-w-2xl mx-auto">
        <!-- Vehicle Name -->
        <div class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border-2 border-blue-200 dark:border-blue-700">
          <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            <span>🏷️</span> Vehicle Name *
          </label>
          <input
            v-model="form.name"
            type="text"
            class="input-field"
            placeholder="e.g., My Tesla, Family Van"
            required
          />
        </div>

        <!-- Make & Model -->
        <div class="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-4 border-2 border-green-200 dark:border-green-700">
          <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            <span>🏭</span> Make & Model
          </label>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">
                Make
              </label>
              <input
                v-model="form.make"
                type="text"
                class="input-field"
                placeholder="e.g., Tesla, Ford"
              />
            </div>
            <div>
              <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">
                Model
              </label>
              <input
                v-model="form.model"
                type="text"
                class="input-field"
                placeholder="e.g., Model 3, Transit"
              />
            </div>
          </div>
          
          <!-- Year -->
          <div class="mt-3">
            <label class="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">
              <span>📅</span> Year
            </label>
            <input
              v-model.number="form.year"
              type="number"
              class="input-field"
              min="1900"
              :max="new Date().getFullYear() + 1"
              placeholder="e.g., 2023"
            />
          </div>
        </div>

        <!-- Fuel Type -->
        <div class="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-lg p-4 border-2 border-orange-200 dark:border-orange-700">
          <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            <span>⛽</span> Fuel Type *
          </label>
          <select v-model="form.fuelType" class="input-field" required>
            <option value="gasoline">⛽ Gasoline</option>
            <option value="diesel">🛢️ Diesel</option>
            <option value="electric">⚡ Electric</option>
            <option value="hybrid">🔋 Hybrid</option>
            <option value="plugin_hybrid">🔌 Plug-in Hybrid</option>
          </select>
        </div>

        <!-- Fuel Consumption (for non-electric) -->
        <div v-if="form.fuelType !== 'electric'" class="bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-lg p-4 border-2 border-cyan-200 dark:border-cyan-700">
          <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            <span>📊</span> Fuel Consumption (L/100km) *
          </label>
          <div class="grid grid-cols-3 gap-2">
            <div>
              <label class="block text-xs text-center text-gray-600 dark:text-gray-400 mb-1">🏙️ City</label>
              <input
                v-model.number="form.fuelConsumptionCity"
                type="number"
                step="0.1"
                class="input-field text-sm text-center"
                placeholder="8.5"
              />
            </div>
            <div>
              <label class="block text-xs text-center text-gray-600 dark:text-gray-400 mb-1">🛣️ Highway</label>
              <input
                v-model.number="form.fuelConsumptionHighway"
                type="number"
                step="0.1"
                class="input-field text-sm text-center"
                placeholder="6.5"
              />
            </div>
            <div>
              <label class="block text-xs text-center text-gray-600 dark:text-gray-400 mb-1">⚖️ Combined*</label>
              <input
                v-model.number="form.fuelConsumptionCombined"
                type="number"
                step="0.1"
                class="input-field text-sm text-center"
                placeholder="7.5"
                required
              />
            </div>
          </div>
        </div>

        <!-- Tank Capacity (for non-electric) -->
        <div v-if="form.fuelType !== 'electric'" class="bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg p-4 border-2 border-yellow-200 dark:border-yellow-700">
          <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            <span>⛽</span> Tank Capacity (L)
          </label>
          <input
            v-model.number="form.tankCapacity"
            type="number"
            step="1"
            class="input-field"
            placeholder="e.g., 60"
          />
        </div>

        <!-- Electric Vehicle Fields -->
        <div v-if="form.fuelType === 'electric' || form.fuelType === 'plugin_hybrid'" class="bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 rounded-lg p-4 border-2 border-green-200 dark:border-green-700 space-y-3">
          <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            <span>⚡</span> Electric Vehicle Specs
          </label>
          
          <div>
            <label class="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">
              <span>🔋</span> Battery Capacity (kWh) {{ form.fuelType === 'electric' ? '*' : '' }}
            </label>
            <input
              v-model.number="form.batteryCapacity"
              type="number"
              step="0.1"
              class="input-field"
              placeholder="e.g., 75"
              :required="form.fuelType === 'electric'"
            />
          </div>

          <div>
            <label class="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">
              <span>📏</span> Range (km) {{ form.fuelType === 'electric' ? '*' : '' }}
            </label>
            <input
              v-model.number="form.rangeElectric"
              type="number"
              step="1"
              class="input-field"
              placeholder="e.g., 500"
              :required="form.fuelType === 'electric'"
            />
          </div>

          <div>
            <label class="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">
              <span>⚡</span> Fast Charging Speed (kW)
            </label>
            <input
              v-model.number="form.chargingSpeed"
              type="number"
              step="1"
              class="input-field"
              placeholder="e.g., 250"
            />
          </div>

          <div v-if="form.fuelType === 'electric'">
            <label class="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">
              <span>📊</span> Energy Consumption (kWh/100km) *
            </label>
            <input
              v-model.number="form.fuelConsumptionCombined"
              type="number"
              step="0.1"
              class="input-field"
              placeholder="e.g., 18"
              required
            />
          </div>
        </div>

        <!-- Display Options -->
        <div class="bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 rounded-lg p-4 border-2 border-pink-200 dark:border-pink-700">
          <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            <span>🎨</span> Display Options
          </label>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">
                Color
              </label>
              <input
                v-model="form.color"
                type="color"
                class="input-field h-10"
            />
          </div>
            <div>
              <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">
                Icon
              </label>
              <select v-model="form.icon" class="input-field">
                <option value="🚗">🚗 Car</option>
                <option value="🚙">🚙 SUV</option>
                <option value="🚐">🚐 Van</option>
                <option value="🏎️">🏎️ Sports Car</option>
                <option value="🚕">🚕 Sedan</option>
                <option value="⚡">⚡ Electric</option>
                <option value="🔋">🔋 Hybrid</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Set as Default -->
        <div class="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg p-4 border-2 border-indigo-200 dark:border-indigo-700">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="form.isDefault"
              type="checkbox"
              id="isDefault"
              class="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
            />
            <span class="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <span>⭐</span> Set as default vehicle
            </span>
          </label>
        </div>

        <!-- Notes -->
        <div class="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800/50 dark:to-slate-800/50 rounded-lg p-4 border-2 border-gray-200 dark:border-gray-700">
          <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            <span>📝</span> Notes
          </label>
          <textarea
            v-model="form.notes"
            class="input-field"
            rows="3"
            placeholder="Additional notes about this vehicle..."
          ></textarea>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-3 pt-4">
          <button
            type="button"
            @click="$emit('close')"
            class="btn-secondary flex-1 font-semibold"
          >
            ❌ Cancel
          </button>
          <button
            type="submit"
            class="btn-primary flex-1 font-semibold shadow-lg"
            :disabled="isLoading"
          >
            {{ isLoading ? '⏳ Saving...' : (vehicle ? '💾 Update' : '➕ Add Vehicle') }}
          </button>
        </div>
      </form>
    </ion-content>
  </ion-modal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent
} from '@ionic/vue'
import { closeOutline } from 'ionicons/icons'
import type { Vehicle, FuelType } from '@/types'

interface Props {
  isOpen: boolean
  vehicle?: Vehicle | null
}

const props = withDefaults(defineProps<Props>(), {
  vehicle: null
})

const emit = defineEmits<{
  close: []
  save: [vehicleData: Partial<Vehicle>]
}>()

const isLoading = ref(false)

const form = ref({
  name: '',
  make: '',
  model: '',
  year: undefined as number | undefined,
  fuelType: 'gasoline' as FuelType,
  fuelConsumptionCity: undefined as number | undefined,
  fuelConsumptionHighway: undefined as number | undefined,
  fuelConsumptionCombined: 8.0,
  tankCapacity: undefined as number | undefined,
  batteryCapacity: undefined as number | undefined,
  chargingSpeed: undefined as number | undefined,
  rangeElectric: undefined as number | undefined,
  color: '#3b82f6',
  icon: '🚗',
  isDefault: false,
  notes: ''
})

watch(() => props.vehicle, (vehicle) => {
  if (vehicle) {
    form.value = {
      name: vehicle.name,
      make: vehicle.make || '',
      model: vehicle.model || '',
      year: vehicle.year,
      fuelType: vehicle.fuelType,
      fuelConsumptionCity: vehicle.fuelConsumptionCity,
      fuelConsumptionHighway: vehicle.fuelConsumptionHighway,
      fuelConsumptionCombined: vehicle.fuelConsumptionCombined,
      tankCapacity: vehicle.tankCapacity,
      batteryCapacity: vehicle.batteryCapacity,
      chargingSpeed: vehicle.chargingSpeed,
      rangeElectric: vehicle.rangeElectric,
      color: vehicle.color || '#3b82f6',
      icon: vehicle.icon || '🚗',
      isDefault: vehicle.isDefault,
      notes: vehicle.notes || ''
    }
  } else {
    // Reset form
    form.value = {
      name: '',
      make: '',
      model: '',
      year: undefined,
      fuelType: 'gasoline',
      fuelConsumptionCity: undefined,
      fuelConsumptionHighway: undefined,
      fuelConsumptionCombined: 8.0,
      tankCapacity: undefined,
      batteryCapacity: undefined,
      chargingSpeed: undefined,
      rangeElectric: undefined,
      color: '#3b82f6',
      icon: '🚗',
      isDefault: false,
      notes: ''
    }
  }
}, { immediate: true })

function handleSubmit() {
  isLoading.value = true
  
  const vehicleData: Partial<Vehicle> = {
    name: form.value.name,
    make: form.value.make || undefined,
    model: form.value.model || undefined,
    year: form.value.year,
    fuelType: form.value.fuelType,
    fuelConsumptionCity: form.value.fuelConsumptionCity,
    fuelConsumptionHighway: form.value.fuelConsumptionHighway,
    fuelConsumptionCombined: form.value.fuelConsumptionCombined,
    tankCapacity: form.value.tankCapacity,
    batteryCapacity: form.value.batteryCapacity,
    chargingSpeed: form.value.chargingSpeed,
    rangeElectric: form.value.rangeElectric,
    color: form.value.color,
    icon: form.value.icon,
    isDefault: form.value.isDefault,
    notes: form.value.notes || undefined
  }

  emit('save', vehicleData)
  isLoading.value = false
}
</script>

<style scoped>
.input-field {
  @apply w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
         bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
         focus:ring-2 focus:ring-primary-500 focus:border-transparent
         transition-colors;
}

.btn-primary {
  @apply px-4 py-2 bg-primary-600 text-white rounded-lg font-medium
         hover:bg-primary-700 focus:ring-4 focus:ring-primary-200
         disabled:opacity-50 disabled:cursor-not-allowed
         transition-colors;
}

.btn-secondary {
  @apply px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100
         rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600
         transition-colors;
}
</style>
